import { Button, PulseDot } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import logoUniverseit from "@/assets/logo-universeit-white.png";
import m365Logo from "@/assets/m365-logo-blog.png.asset.json";
import m365LogoHeader from "@/assets/m365-logo-header-v2.png.asset.json";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { services } from "@/lib/serviceLinks";
import PromoBanner from "@/components/PromoBanner";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  // Reserve real layout space equal to the fixed header's actual height
  // (banner + nav). Prevents the hero from being clipped on mobile when the
  // promo banner wraps to two lines.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      document.body.style.setProperty("--header-offset", `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // "About Us" is an internal route — rendered separately below (not part of this list).


  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md"
      style={{
        backgroundColor: "rgba(5, 16, 14, 0.72)",
        borderBottom: "1px solid rgba(72, 150, 146, 0.14)",
      }}
    >
      <PromoBanner />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center justify-between md:justify-normal h-16 lg:h-20 relative">
          {/* Far left: UniverseIT logo + M365 lockup */}
          <div className="hidden md:flex items-center gap-3 flex-none min-w-0">
            <a
              href="https://universeit.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
            >
              <img
                src={logoUniverseit}
                alt="UniverseIT - Beyond the Cloud"
                className="h-8 lg:h-12 w-auto"
                width="138"
                height="32"
              />
            </a>
            <span aria-hidden="true" className="h-8 w-px bg-[hsl(var(--border))]" />
            <Link to="/" aria-label="Microsoft 365 home" className="inline-flex">
              <img
                src={m365LogoHeader.url}
                alt="Microsoft 365"
                className="h-6 lg:h-8 w-auto rounded-md bg-white/95 px-2 py-1"
              />
            </Link>
          </div>

          {/* Center: Desktop Navigation */}
          <nav className="hidden md:flex items-center justify-center md:space-x-5 lg:space-x-8">
            {/* Services Dropdown */}
            <DropdownMenu open={isServicesOpen} onOpenChange={setIsServicesOpen}>
              <DropdownMenuTrigger
                asChild
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <Link
                  to="/"
                  className="flex items-center gap-1 text-foreground hover:text-primary transition-smooth font-medium outline-none"
                >
                  Services
                  <ChevronDown className={`h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </Link>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-card border border-cosmic-strong shadow-strong z-50 min-w-[240px]"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                {(["entry", "onboarding", "services"] as const).map((group) => {
                  const items = services.filter((s) => s.group === group);
                  if (items.length === 0) return null;
                  const label =
                    group === "entry"
                      ? "Free entry point"
                      : group === "onboarding"
                        ? "Onboarding"
                        : "Services";
                  return (
                    <div key={group}>
                      <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                        {label}
                      </div>
                      {items.map((item) => (
                        <DropdownMenuItem key={item.name} asChild>
                          <Link to={item.href} className="w-full cursor-pointer">
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Solutions — plain link, no dropdown */}
            <Link
              to="/solutions"
              className="text-foreground hover:text-primary transition-smooth font-medium"
            >
              Solutions
            </Link>

            {/* Microsoft Licensing — external link to universeit.com */}
            <a
              href="https://universeit.com/microsoft-licensing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:text-primary transition-smooth font-medium"
            >
              Microsoft Licensing
            </a>

            {/* About Us — internal route */}
            <Link
              to="/about-us"
              className="text-foreground hover:text-primary transition-smooth font-medium"
            >
              About Us
            </Link>

            {/* Blog — external link to universeit.com, paired with M365 logo */}
            <a
              href="https://universeit.com/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-smooth font-medium"
            >
              <img
                src={m365Logo.url}
                alt="Microsoft 365"
                className="h-5 w-auto"
              />
              Blog
            </a>
          </nav>

          {/* Far right: CTA */}
          <div className="hidden md:flex items-center justify-end">
            <Button variant="cosmic" asChild>
              <a href="https://universeit.com" target="_blank" rel="noopener noreferrer">
                <PulseDot />
                Chat with us now
              </a>
            </Button>
          </div>

          {/* Mobile: UniverseIT logo (linked) + M365 lockup */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://universeit.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={logoUniverseit}
                alt="UniverseIT - Beyond the Cloud"
                className="h-8 w-auto"
                width="138"
                height="32"
              />
            </a>
            <span aria-hidden="true" className="h-6 w-px bg-[hsl(var(--border))]" />
            <Link to="/" aria-label="Microsoft 365 home" className="inline-flex">
              <img
                src={m365LogoHeader.url}
                alt="Microsoft 365"
                className="h-5 w-auto rounded bg-white/95 px-1.5 py-0.5"
              />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-foreground"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-background border-t border-cosmic">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Services with expandable submenu — first */}
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="flex items-center justify-between w-full px-3 py-2 text-foreground hover:text-primary transition-smooth font-medium"
                >
                  Services
                  <ChevronDown className={`h-4 w-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileServicesOpen && (
                  <div className="pl-6 space-y-1 bg-muted/30 rounded-md mx-2">
                    {services.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block px-3 py-2 text-foreground hover:text-primary transition-smooth font-medium text-sm"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Solutions — plain link */}
              <Link
                to="/solutions"
                className="block px-3 py-2 text-foreground hover:text-primary transition-smooth font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Solutions
              </Link>

              {/* Microsoft Licensing — external link */}
              <a
                href="https://universeit.com/microsoft-licensing"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2 text-foreground hover:text-primary transition-smooth font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Microsoft Licensing
              </a>

              {/* About Us — internal route */}
              <Link
                to="/about-us"
                className="block px-3 py-2 text-foreground hover:text-primary transition-smooth font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>

              {/* Blog — external link to universeit.com, paired with M365 logo */}
              <a
                href="https://universeit.com/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 text-foreground hover:text-primary transition-smooth font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                <img
                  src={m365Logo.url}
                  alt="Microsoft 365"
                  className="h-5 w-auto"
                />
                Blog
              </a>

              <div className="px-3 py-2">
                <Button variant="cosmic" className="w-full" asChild>
                  <a href="https://universeit.com" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>
                    <PulseDot />
                    Chat with us now
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
